import { Box, Drawer, IconButton, Typography, useMediaQuery, useTheme } from "@mui/material";
import LibraryMusicIcon from "@mui/icons-material/LibraryMusic";
import CloseIcon from "@mui/icons-material/Close";
import CreatePlaylistButton from "./CreatePlaylistButton";
import PlaylistView from "./PlaylistView";
import React, { useEffect } from "react";
import PlaylistRow from "./PlaylistRow";
import Playlist from "../../models/Playlist";
import { fetchPlaylists, PLAYLISTS_CHANGED_EVENT } from "../../services/playlist";
import { colors } from "../../theme";

export default function PanelsFrame() {
    const [playlistDetails, setPlaylistDetails] = React.useState({});
    const [playlists, setPlaylists] = React.useState<Playlist[]>([]);
    // Below `sm` the library and the playlist pane can't both fit - the library
    // becomes a slide-over instead of a permanent column, so the pane always gets
    // the full width. At `sm` and up nothing here changes from before.
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [mobileLibraryOpen, setMobileLibraryOpen] = React.useState(false);

    const loadPlaylists = React.useCallback(async () => {
        try {
            setPlaylists(await fetchPlaylists());
        } catch (error) {
            console.error("Error fetching playlists:", error);
        }
    }, []);

    useEffect(() => {
        loadPlaylists();
    }, [loadPlaylists]);

    // Following someone's playlist from the header search has to show up here, and
    // the search UI has no path to this state - it announces the change instead.
    useEffect(() => {
        window.addEventListener(PLAYLISTS_CHANGED_EVENT, loadPlaylists);
        return () => window.removeEventListener(PLAYLISTS_CHANGED_EVENT, loadPlaylists);
    }, [loadPlaylists]);

    const handlePlaylistCreated = (playlist: Playlist) => {
        setPlaylists(prev => [...prev, playlist]);
        // Brand new, so it has no tracks yet - no need to round-trip to
        // getPlaylistDetails (which currently 401s on an empty playlist anyway).
        setPlaylistDetails({
            playlistName: playlist.playlistName,
            playlistId: playlist.id,
            imageUrl: playlist.imageUrl,
            tracks: [],
            // Nobody follows or contributes to a playlist that was created a moment ago.
            owned: true,
            following: false,
            contributor: false,
            contributorRequested: false,
            isPublic: playlist.isPublic,
        });
        if (isMobile) setMobileLibraryOpen(false);
    };

    // Selecting a playlist from the slide-over should also dismiss it, so picking
    // one immediately shows the result instead of leaving the drawer covering it.
    const handlePlaylistSelected = (details: any) => {
        setPlaylistDetails(details);
        if (isMobile) setMobileLibraryOpen(false);
    };

    const libraryContent = (
        <>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 2.5,
                    py: 2,
                    flexShrink: 0,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                    <LibraryMusicIcon sx={{ color: colors.textSecondary, fontSize: 22 }} />
                    <Typography sx={{ fontWeight: 700, fontSize: 15, color: colors.textSecondary }}>
                        Your Library
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <CreatePlaylistButton onCreated={handlePlaylistCreated} />
                    {isMobile && (
                        <IconButton
                            onClick={() => setMobileLibraryOpen(false)}
                            aria-label="Close library"
                            sx={{ color: colors.textSecondary }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    )}
                </Box>
            </Box>

            <Box sx={{ flexGrow: 1, overflowY: "auto", px: 1, pb: 1 }}>
                {playlists.length > 0 ? (
                    playlists.map((playlist: Playlist) => (
                        <PlaylistRow
                            key={playlist.id}
                            playlist={playlist}
                            ownerId={playlist.ownerId}
                            setPlaylistDetails={handlePlaylistSelected}
                        />
                    ))
                ) : (
                    <Box
                        sx={{
                            m: 1,
                            p: 2.5,
                            borderRadius: 2,
                            backgroundColor: colors.elevated,
                        }}
                    >
                        <Typography sx={{ fontWeight: 700, fontSize: 15, mb: 0.5 }}>
                            Create your first playlist
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: 13 }}>
                            Use the + button to start one — or search for someone up top
                            and follow a playlist they've made.
                        </Typography>
                    </Box>
                )}
            </Box>
        </>
    );

    return (
        <Box
            sx={{
                display: "flex",
                gap: 1,
                height: "100%",
                p: 1,
                backgroundColor: colors.sidebar,
            }}
        >
            {isMobile ? (
                <Drawer
                    open={mobileLibraryOpen}
                    onClose={() => setMobileLibraryOpen(false)}
                    ModalProps={{ keepMounted: true }}
                    PaperProps={{
                        sx: {
                            width: 280,
                            maxWidth: "85vw",
                            backgroundColor: colors.base,
                            display: "flex",
                            flexDirection: "column",
                        },
                    }}
                >
                    {libraryContent}
                </Drawer>
            ) : (
                /* ---- Library sidebar ---- */
                <Box
                    component="aside"
                    sx={{
                        width: { sm: 260, lg: 300 },
                        flexShrink: 0,
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: 2,
                        backgroundColor: colors.base,
                        overflow: "hidden",
                    }}
                >
                    {libraryContent}
                </Box>
            )}

            {/* ---- Main content ---- */}
            <Box sx={{ flexGrow: 1, minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column" }}>
                {isMobile && (
                    <IconButton
                        onClick={() => setMobileLibraryOpen(true)}
                        aria-label="Open library"
                        sx={{
                            alignSelf: "flex-start",
                            m: 1,
                            color: colors.textSecondary,
                            backgroundColor: colors.base,
                            "&:hover": { backgroundColor: colors.elevated },
                        }}
                    >
                        <LibraryMusicIcon fontSize="small" />
                    </IconButton>
                )}
                <PlaylistView
                    playlistDetails={playlistDetails}
                    setPlaylistDetails={setPlaylistDetails}
                />
            </Box>
        </Box>
    );
}
